/**
 * Årshjul Store - Zustand med Cosmos DB-synk
 * Hanterar återkommande påminnelser och underhållsuppgifter för beredskap
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from './auth-store';
import { apiGet, apiPut, apiPost, apiDelete } from '@/services/api-client';
import { logYearWheelAction } from '@/lib/activity-logger';

export type TaskFrequency = 'monthly' | 'quarterly' | 'biannual' | 'annual';
export type TaskDeadline = 'lastDay' | 'day21' | 'day14' | 'day7';
export type PrepArea = 'water' | 'food' | 'energy' | 'health' | 'information' | 'shelter' | 'transport' | 'communication' | 'safety' | 'shopping' | 'gardening' | 'other';

export const DEFAULT_TASK_DEADLINE: TaskDeadline = 'lastDay';

export interface RecurringTask {
  id: string;
  title: string;
  description: string;
  area: PrepArea;
  frequency: TaskFrequency;
  month: number; // 0-11 för januari-december
  deadlineType: TaskDeadline;
  lastCompleted: string | null;
  nextDue: string;
  isCustom: boolean;
  notifyDaysBefore: number;
  enabled: boolean;
  tags: string[]; // Max 5 tags per task
  createdAt: string;
  updatedAt: string;
}

export interface TaskCompletion {
  taskId: string;
  completedAt: string;
  notes?: string;
}

interface YearWheelState {
  tasks: RecurringTask[];
  completionHistory: TaskCompletion[];
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncAt: string | null;
  syncError: string | null;
  
  // Actions
  addTask: (task: Omit<RecurringTask, 'id' | 'createdAt' | 'updatedAt' | 'nextDue' | 'lastCompleted'>) => Promise<void>;
  updateTask: (id: string, updates: Partial<RecurringTask>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  completeTask: (id: string, notes?: string, completedAt?: string) => Promise<void>;
  toggleTaskEnabled: (id: string) => Promise<void>;
  createDefaultTasks: () => Promise<void>;
  
  // Getters
  getUpcomingTasks: (days: number) => RecurringTask[];
  getOverdueTasks: () => RecurringTask[];
  getTasksByArea: (area: PrepArea) => RecurringTask[];
  getTasksByMonth: (month: number, year?: number) => RecurringTask[];
  
  // Sync
  fetchFromServer: () => Promise<void>;
  syncToServer: () => Promise<void>;
  
  // Internal
  _addTaskToServer: (task: RecurringTask) => Promise<void>;
  _updateTaskOnServer: (id: string, updates: Partial<RecurringTask>) => Promise<void>;
  _deleteTaskOnServer: (id: string) => Promise<void>;
  _completeTaskOnServer: (id: string, notes?: string, completedAt?: string) => Promise<void>;
  
  // Hydration
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// Frekvens-etiketter
export const frequencyLabels: Record<TaskFrequency, string> = {
  monthly: 'Varje månad',
  quarterly: 'Varje kvartal',
  biannual: 'Var 6:e månad',
  annual: 'Årligen',
};

export const deadlineLabels: Record<TaskDeadline, string> = {
  lastDay: 'Sista dagen',
  day21: '21:a',
  day14: '14:e',
  day7: '7:e',
};

// ============================================================================
// Helper Functions
// ============================================================================

function getDeadlineDay(deadlineType: TaskDeadline, year: number, month: number): number {
  if (deadlineType === 'lastDay') {
    return new Date(year, month + 1, 0).getDate();
  }

  switch (deadlineType) {
    case 'day21':
      return 21;
    case 'day14':
      return 14;
    case 'day7':
      return 7;
    default:
      return new Date(year, month + 1, 0).getDate();
  }
}

function getOccurrenceDueDate(year: number, month: number, deadlineType: TaskDeadline): Date {
  return new Date(year, month, getDeadlineDay(deadlineType, year, month));
}

function toYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseYmd(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, month, day);

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
}

function preserveOccurrenceMonthNextDue(currentNextDue: string, deadlineType: TaskDeadline): string {
  const parsed = parseYmd(currentNextDue);
  if (!parsed) return currentNextDue;

  return toYmd(getOccurrenceDueDate(parsed.getFullYear(), parsed.getMonth(), deadlineType));
}

function calculateNextDue(
  month: number,
  frequency: TaskFrequency = 'monthly',
  deadlineType: TaskDeadline = DEFAULT_TASK_DEADLINE,
  fromDate?: Date,
): string {
  const today = fromDate ? new Date(fromDate) : new Date();
  today.setHours(0, 0, 0, 0);

  const intervalMonths = (() => {
    switch (frequency) {
      case 'monthly': return 1;
      case 'quarterly': return 3;
      case 'biannual': return 6;
      case 'annual': return 12;
      default: return 1;
    }
  })();

  const startIndex = today.getFullYear() * 12 + today.getMonth();
  for (let i = 0; i < 120; i++) {
    const idx = startIndex + i;
    const year = Math.floor(idx / 12);
    const m = idx % 12;
    if (((m - month + 12) % intervalMonths) === 0) {
      const occurrenceDue = getOccurrenceDueDate(year, m, deadlineType);
      if (occurrenceDue >= today) {
        return toYmd(occurrenceDue);
      }
    }
  }

  const fallback = new Date();
  fallback.setMonth(fallback.getMonth() + 1);
  const fallbackDue = getOccurrenceDueDate(
    fallback.getFullYear(),
    fallback.getMonth(),
    deadlineType,
  );
  return toYmd(fallbackDue);
}

// ============================================================================
// Store
// ============================================================================

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

export const useYearWheelStore = create<YearWheelState>()(
  persist(
    (set, get) => ({
      tasks: [],
      completionHistory: [],
      isLoading: false,
      isSyncing: false,
      lastSyncAt: null,
      syncError: null,
      
      // ========================================================================
      // Actions
      // ========================================================================
      
      addTask: async (taskData) => {
        const { isAuthenticated } = useAuthStore.getState();
        
        const newTask: RecurringTask = {
          ...taskData,
          id: generateId(),
          lastCompleted: null,
          nextDue: calculateNextDue(taskData.month, taskData.frequency, taskData.deadlineType),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        // Lägg till lokalt först
        set((state) => ({ tasks: [...state.tasks, newTask] }));
        
        // Synka till server om inloggad
        if (isAuthenticated) {
          try {
            await get()._addTaskToServer(newTask);
            // Hämta uppdaterad version från server (med korrekt nextDue)
            await get().fetchFromServer();
          } catch (error) {
            console.error('Kunde inte synka ny uppgift till server:', error);
            set({ syncError: 'Kunde inte spara uppgift till servern' });
          }
        }
      },
      
      updateTask: async (id, updates) => {
        const { isAuthenticated } = useAuthStore.getState();
        
        // Uppdatera lokalt först
        set((state) => ({
          tasks: state.tasks.map((task) => {
            if (task.id !== id) return task;

            const updatedTask: RecurringTask = {
              ...task,
              ...updates,
              updatedAt: new Date().toISOString(),
            };

            if (updates.nextDue !== undefined) {
              return updatedTask;
            }

            const currentDeadlineType = task.deadlineType || DEFAULT_TASK_DEADLINE;
            const updatedDeadlineType = updatedTask.deadlineType || DEFAULT_TASK_DEADLINE;
            const monthChanged = updates.month !== undefined && updates.month !== task.month;
            const frequencyChanged = updates.frequency !== undefined && updates.frequency !== task.frequency;
            const deadlineChanged = updates.deadlineType !== undefined && updatedDeadlineType !== currentDeadlineType;

            if (monthChanged || frequencyChanged) {
              updatedTask.nextDue = calculateNextDue(updatedTask.month, updatedTask.frequency, updatedDeadlineType);
            } else if (deadlineChanged) {
              updatedTask.nextDue = preserveOccurrenceMonthNextDue(task.nextDue, updatedDeadlineType);
            }

            return updatedTask;
          }),
        }));
        
        // Synka till server om inloggad
        if (isAuthenticated) {
          try {
            await get()._updateTaskOnServer(id, updates);
            // Efter lyckad uppdatering, hämta canonical state från server
            await get().fetchFromServer();
          } catch (error) {
            console.error('Kunde inte synka uppdatering till server:', error);
            set({ syncError: 'Kunde inte uppdatera uppgift på servern' });
          }
        }
      },
      
      deleteTask: async (id) => {
        const { isAuthenticated } = useAuthStore.getState();
        
        // Ta bort lokalt först (optimistisk uppdatering)
        set((state) => ({
          tasks: state.tasks.filter((task) => task.id !== id),
        }));
        
        // Synka till server om inloggad
        if (isAuthenticated) {
          try {
            await get()._deleteTaskOnServer(id);
            // Lokal state är redan uppdaterad, ingen fetch behövs
          } catch (error) {
            console.error('Kunde inte synka radering till server:', error);
            set({ syncError: 'Kunde inte ta bort uppgift från servern' });
            // Vid fel, hämta från server för att återställa korrekt state
            await get().fetchFromServer();
          }
        }
      },
      
      completeTask: async (id, notes, completedAt) => {
        const { isAuthenticated } = useAuthStore.getState();
        const now = completedAt ? new Date(completedAt) : new Date();

        // Optimistisk uppdatering: markera lokalt som utförd
        const completion: TaskCompletion = {
          taskId: id,
          completedAt: now.toISOString(),
          notes,
        };

        // Save previous state to allow revert on failure
        const prevTasks = get().tasks;
        const prevHistory = get().completionHistory;

        set((state) => ({
          completionHistory: [completion, ...state.completionHistory].slice(0, 100),
          tasks: state.tasks.map((t) => {
            if (t.id !== id) return t;
            // If the user completes a future occurrence in advance, base the next due
            // calculation on the month after the current nextDue (not today), so that
            // nextDue advances past the completed occurrence rather than staying in the past.
            const taskCurrentNextDue = new Date(t.nextDue);
            const completionBase = taskCurrentNextDue > now
              ? new Date(taskCurrentNextDue.getFullYear(), taskCurrentNextDue.getMonth() + 1, 1)
              : now;
            return {
              ...t,
              lastCompleted: now.toISOString(),
              nextDue: calculateNextDue(t.month, t.frequency, t.deadlineType, completionBase),
              updatedAt: new Date().toISOString(),
            };
          }),
        }));

        // Synka till server om inloggad
        if (isAuthenticated) {
          try {
            await get()._completeTaskOnServer(id, notes, completedAt);
            // Hämta från server i bakgrunden för att få canonical state, men vänta inte på det
            get().fetchFromServer().catch(console.error);
          } catch (error) {
            console.error('Kunde inte synka slutförande till server:', error);
            // Revertera till tidigare lokalt state
            set({ tasks: prevTasks, completionHistory: prevHistory, syncError: 'Kunde inte markera uppgift som slutförd på servern' });
          }
        }
      },
      
      toggleTaskEnabled: async (id) => {
        const task = get().tasks.find(t => t.id === id);
        if (task) {
          await get().updateTask(id, { enabled: !task.enabled });
        }
      },
      
      createDefaultTasks: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        
        if (!isAuthenticated) return;
        
        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const familyId = getEffectiveFamilyOwnerId() || useAuthStore.getState().user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';

          const res = await apiPost(`/year-wheel/default${qs}`);
          if (!res || !(res as unknown as { success?: boolean }).success) {
            const err = (res && (res as unknown as { error?: string }).error) || 'Okänt API-fel vid skapande av standarduppgifter';
            console.error('API error createDefaultTasks:', err, res);
            throw new Error(String(err));
          }
          // Hämta uppdaterade uppgifter från server
          await get().fetchFromServer();
        } catch (error) {
          console.error('Kunde inte skapa standarduppgifter:', error);
          set({ syncError: 'Kunde inte skapa standarduppgifter' });
        }
      },
      // Getters
      // ========================================================================
      
      getUpcomingTasks: (days) => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const futureDate = new Date();
        futureDate.setHours(0, 0, 0, 0);
        futureDate.setDate(futureDate.getDate() + (days || 7)); // Default to 7 days if not specified
        
        return get()
          .tasks.filter((task) => {
            if (!task.enabled) return false;
            const dueDate = new Date(task.nextDue);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate >= now && dueDate <= futureDate;
          })
          .sort((a, b) => new Date(a.nextDue).getTime() - new Date(b.nextDue).getTime());
      },
      
      getOverdueTasks: () => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return get()
          .tasks.filter((task) => {
            if (!task.enabled) return false;
            const dueDate = new Date(task.nextDue);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate < now;
          })
          .sort((a, b) => new Date(a.nextDue).getTime() - new Date(b.nextDue).getTime());
      },
      
      getTasksByArea: (area) => {
        return get().tasks.filter((task) => task.area === area && task.enabled);
      },
      
      getTasksByMonth: (month: number, year?: number) => {
        const now = new Date();
        const targetYear = year ?? now.getFullYear();
        
        return get().tasks.filter((task) => {
          if (!task.enabled) return false;

          const baseMonth = task.month;
          const createdDate = new Date(task.createdAt);
          const createdYear = createdDate.getFullYear();

          // Do not show occurrences before the task was created
          if (targetYear < createdYear) return false;
          if (targetYear === createdYear && month < createdDate.getMonth()) return false;

          // Determine whether this month is a scheduled occurrence based on frequency
          let occursThisMonth = false;
          switch (task.frequency) {
            case 'monthly':
              occursThisMonth = targetYear >= createdYear;
              break;
            case 'quarterly':
              occursThisMonth = (month - baseMonth + 12) % 3 === 0 && targetYear >= createdYear;
              break;
            case 'biannual': {
              const secondMonth = (baseMonth + 6) % 12;
              occursThisMonth = (month === baseMonth || month === secondMonth) && targetYear >= createdYear;
              break;
            }
            case 'annual':
              occursThisMonth = month === baseMonth && targetYear >= createdYear;
              break;
            default:
              occursThisMonth = false;
          }

          // Only include scheduled occurrence months. Do not include non-series months even
          // if `lastCompleted` falls there — completed history is shown in the task detail instead.
          return occursThisMonth;
        });
      },
      
      // ========================================================================
      // Sync
      // ========================================================================
      
      fetchFromServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        
        set({ isLoading: true, syncError: null });
        
        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          const response = await apiGet<RecurringTask[]>(`/year-wheel${qs}`);

          if (response && response.success && Array.isArray(response.data)) {
            // Migrera tasks som saknar tags-fältet
            const migratedTasks = response.data.map(task => ({
              ...task,
              deadlineType: task.deadlineType || DEFAULT_TASK_DEADLINE,
              notifyDaysBefore: task.notifyDaysBefore ?? 7,
              tags: task.tags || [],
            }));
            
            set({
              tasks: migratedTasks,
              lastSyncAt: new Date().toISOString(),
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('Kunde inte hämta årshjulsuppgifter från server:', error);
          set({
            syncError: 'Kunde inte hämta uppgifter från servern',
            isLoading: false,
          });
        }
      },
      
      syncToServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        
        set({ isSyncing: true, syncError: null });
        
        try {
          // För årshjul synkar vi genom att hämta från server
          // Servern skapar automatiskt standarduppgifter vid första GET
          await get().fetchFromServer();
          
          set({
            isSyncing: false,
            lastSyncAt: new Date().toISOString(),
          });
        } catch (error) {
          console.error('Kunde inte synkronisera årshjulsuppgifter:', error);
          set({
            syncError: 'Synkronisering misslyckades',
            isSyncing: false,
          });
        }
      },
      
      // ========================================================================
      // Internal Server Methods
      // ========================================================================
      
      _addTaskToServer: async (task) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const familyId = getEffectiveFamilyOwnerId() || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';

        const res = await apiPost(`/year-wheel${qs}`, {
          title: task.title,
          description: task.description,
          area: task.area,
          frequency: task.frequency,
          month: task.month,
          deadlineType: task.deadlineType,
          notifyDaysBefore: task.notifyDaysBefore,
          enabled: task.enabled,
          isCustom: task.isCustom,
          tags: task.tags || [],
        });
        if (!res || !(res as unknown as { success?: boolean }).success) {
          const err = (res && (res as unknown as { error?: string }).error) || 'Okänt API-fel vid skapande av årshjulsuppgift';
          console.error('API error _addTaskToServer:', err, res);
          throw new Error(String(err));
        }
        logYearWheelAction('CREATE_YEAR_WHEEL_TASK', undefined, { title: task.title, area: task.area });
      },
      
      _updateTaskOnServer: async (id, updates) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const familyId = getEffectiveFamilyOwnerId() || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';

        const res = await apiPut(`/year-wheel/${id}${qs}`, updates);
        if (!res || !(res as unknown as { success?: boolean }).success) {
          const err = (res && (res as unknown as { error?: string }).error) || 'Okänt API-fel vid uppdatering av årshjulsuppgift';
          console.error('API error _updateTaskOnServer:', err, res);
          throw new Error(String(err));
        }
        logYearWheelAction('UPDATE_YEAR_WHEEL_TASK', id);
      },
      
      _deleteTaskOnServer: async (id) => {
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const familyId = getEffectiveFamilyOwnerId() || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';

        const res = await apiDelete(`/year-wheel/${id}${qs}`);
        if (!res || !(res as unknown as { success?: boolean }).success) {
          const err = (res && (res as unknown as { error?: string }).error) || 'Okänt API-fel vid radering av årshjulsuppgift';
          console.error('API error _deleteTaskOnServer:', err, res);
          throw new Error(String(err));
        }
        logYearWheelAction('DELETE_YEAR_WHEEL_TASK', id);
      },
      
      _completeTaskOnServer: async (id: string, notes?: string, completedAt?: string) => {
        const body: Record<string, unknown> = { notes };
        if (completedAt) body.completedAt = completedAt;
        const { getEffectiveFamilyOwnerId } = await import('./family-store');
        const familyId = getEffectiveFamilyOwnerId() || useAuthStore.getState().user?.familyId;
        const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';

        const res = await apiPost(`/year-wheel/${id}/complete${qs}`, body);
        if (!res || !(res as unknown as { success?: boolean }).success) {
          const err = (res && (res as unknown as { error?: string }).error) || 'Okänt API-fel vid slutförande av årshjulsuppgift';
          console.error('API error _completeTaskOnServer:', err, res);
          throw new Error(String(err));
        }
        logYearWheelAction('COMPLETE_YEAR_WHEEL_TASK', id, { notes });
      },
      
      // ========================================================================
      // Hydration
      // ========================================================================
      
      _hasHydrated: false,
      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'beredskapshubben-yearwheel',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tasks: state.tasks,
        completionHistory: state.completionHistory,
        lastSyncAt: state.lastSyncAt,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
        
        // Auto-synk från server när användaren är inloggad
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          state?.fetchFromServer();
        }
      },
    }
  )
);

// Helper hooks
export const useUpcomingTasks = (days: number = 30) => 
  useYearWheelStore((state) => state.getUpcomingTasks(days));
export const useOverdueTasks = () => 
  useYearWheelStore((state) => state.getOverdueTasks());

// Hydration hook
export const useYearWheelHydrated = () => useYearWheelStore((state) => state._hasHydrated);
